import React, { ReactElement, useEffect, useState } from 'react'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { Experience, Paginator, Widget } from '@/types'
import { useRouter } from 'next/router'
import { ExperienceService } from '@/lib/services/experience'
import { LoadingSpinner } from '@/components/layout/loading-spinner'
import AppExperienceCard from '@/components/app/experiences/experience-card'
import AppLayout from '@/components/app/layout/app-layout'
import { WidgetService } from '@/lib/services/widget'
import Pagination from '@/components/pagination'
import { useAnalytics } from '@/hooks/use-analytics'
import Head from 'next/head'

export default function AppExperiencesIndex() {
  const router = useRouter()
  const { trackPageView } = useAnalytics()

  const [widget, setWidget] = useState<Widget>()

  const [experiences, setExperiences] = useState<Paginator<Experience>>()

  useEffect(() => {
    const getWidget = async () => {
      const widgetUuid = String(router.query.widgetUuid)
      const widget = await WidgetService.getWidget(widgetUuid)
      setWidget(widget)
    }

    const getExperiences = async () => {
      const page = Number(router.query.page ?? 1)
      const q = (router.query.q ?? '').toString()
      const widgetUuid = String(router.query.widgetUuid)
      const categoryId = router.query.category_id?.toString()

      try {
        const experiences = await ExperienceService.getAppWidgetExperiences({
          widgetUuid,
          q,
          page,
          categoryId: categoryId,
          limit: 30,
        })
        setExperiences(experiences)
      } catch (e: any) {}
    }

    getWidget()

    getExperiences()
  }, [router.query, router.locale])

  // Track widget page view once widget is loaded
  useEffect(() => {
    if (widget) {
      trackPageView(`/app/widgets/${widget.uuid}`, {
        widgetId: widget.uuid,
        widgetName: widget.name,
        sellerId: widget.company.id.toString(),
        sellerName: widget.company.name,
        platform: 'widget',
        experienceCount: experiences?.meta.total || 0,
      })
    }
  }, [widget, experiences, trackPageView])

  if (!experiences || !widget) {
    return <LoadingSpinner />
  }

  return (
    <>
      <Head>
        <title>
          {widget.name} - {widget.company?.name}
        </title>
        <meta name="google" content="notranslate" />
        <meta httpEquiv="content-language" content={router.locale || 'en'} />
      </Head>
      <div translate="no" className="notranslate">
        <div className="text-3xl font-bold mb-6">{widget.name}</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          {experiences.data.map((experience) => (
            <AppExperienceCard experience={experience} key={experience.id} />
          ))}
        </div>

        <Pagination lastPage={experiences.meta.last_page} />
      </div>
    </>
  )
}

AppExperiencesIndex.getLayout = function getLayout(page: ReactElement) {
  return <AppLayout widget={page.props.widget}>{page}</AppLayout>
}

export async function getServerSideProps({
  locale,
  params,
}: {
  locale: string
  params: Record<string, string>
}) {
  const widget = await WidgetService.getWidget(params.widgetUuid)

  return {
    props: {
      widget,
      ...(await serverSideTranslations(locale, ['common', 'experience'])),
    },
  }
}
